import {
    Badge,
    Button,
    Drawer,
    DrawerProps,
    Form,
    Input,
    InputNumber,
    message,
    Select,
    Spin,
    Switch,
} from 'antd';
import Style from './index.module.scss';
import {useMutation, useQuery} from 'react-query';
import {McpFunctionReturns} from './service';
import {PlusSquareOutlined} from '@ant-design/icons';
import {LinkButton} from '../../../../../Features/Buttons/LinkButton';
import {useWatch} from 'antd/es/form/Form';
import {useMemo, useRef} from 'react';
import {isEqual} from 'lodash-es';
import {DeleteOutline} from 'antd-mobile-icons';
import {Mcp} from '../../service';

interface Props extends DrawerProps {
    onOk: () => void;
    functionId: string;
}

const InputProps = {
    style: {
        width: '100%',
    },
};

const Index = ({onOk, functionId, ...props}: Props) => {
    const [form] = Form.useForm();
    const initialRef = useRef<Record<string, any>>({});

    const getData = useQuery({
        queryKey: ['McpFunctionReturns.list', functionId],
        queryFn: () => McpFunctionReturns.list({funcId: functionId}),
        onSuccess: res => {
            const map: Record<string, any> = {};
            res.forEach(item => {
                if (item.id) {
                    map[item.id] = structuredClone(item);
                }
            });
            initialRef.current = map;
            form.setFieldValue('formData', res);
        },
    });

    function isRowDirty(row: any) {
        if (!row) return false;

        // 本地新增
        if (!row.id) return true;

        const initial = initialRef.current[row.id];
        if (!initial) return true;

        return !isEqual(initial, row);
    }

    const onDelete = useMutation({
        mutationFn: McpFunctionReturns.remove,
        onSuccess: () => {
            message.success('成功!');
            getData.refetch();
        },
    });

    const onSave = useMutation({
        mutationFn: McpFunctionReturns.save,
        onSuccess: () => {
            message.success('成功!');
            getData.refetch();
        },
    });

    function onFinish({formData}: {formData: McpFunctionReturns.Schema[]}) {
        onSave.mutate({
            formData,
            functionId,
        });
    }

    const isAppend: McpFunctionReturns.Schema[] = useWatch('formData', form);

    function Extra() {
        return (
            <Button
                type="primary"
                onClick={form.submit}
                disabled={isAppend?.length < 1}>
                保存
            </Button>
        );
    }

    const haveUnSafe = useMemo(() => {
        return isAppend?.some(item => {
            return !item?.id;
        });
    }, [isAppend]);

    function checkDelete(item: McpFunctionReturns.Schema, noIdCb: () => void) {
        if (item?.id) {
            if (haveUnSafe) {
                return message.info('存在 非安全数据，解决后再删除');
            } else {
                onDelete.mutate(item?.id);
            }
        } else {
            return noIdCb();
        }
    }

    return (
        <Drawer
            title={
                <div className={Style.tips}>
                    <span>Mcp 返回值</span> 【
                    <Badge status="success" text="安全数据" />
                    <Badge status="error" text="非安全数据" /> 】
                </div>
            }
            width="90%"
            extra={<Extra />}
            {...props}>
            <div className={Style.root}>
                <Spin
                    spinning={
                        getData.isLoading ||
                        onSave.isLoading ||
                        onDelete.isLoading
                    }>
                    <Form
                        onFinish={onFinish}
                        form={form}
                        initialValues={{formData: []}}>
                        <Form.List name="formData">
                            {(fields, {add, remove}) => {
                                return (
                                    <div>
                                        <table className={Style.table}>
                                            <colgroup>
                                                <col width={150} />
                                                <col width={150} />
                                                <col width={200} />
                                                <col width={200} />
                                                <col width={150} />
                                                <col width={150} />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th>名称</th>
                                                    <th>类型</th>
                                                    <th>描述</th>
                                                    <th>示例值</th>
                                                    <th>必填</th>
                                                    <th>排序</th>
                                                    <th />
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {fields.map(
                                                    ({
                                                        key,
                                                        name,
                                                        ...restField
                                                    }) => {
                                                        const row =
                                                            form.getFieldValue([
                                                                'formData',
                                                                name,
                                                            ]);
                                                        const dirty =
                                                            isRowDirty(row);

                                                        const rowClass = dirty
                                                            ? Style.rowDirty // 绿色
                                                            : Style.rowClean; // 红色

                                                        return (
                                                            <tr
                                                                key={key}
                                                                className={
                                                                    rowClass
                                                                }>
                                                                <td>
                                                                    <Form.Item
                                                                        hidden
                                                                        noStyle
                                                                        label="id"
                                                                        name={[
                                                                            name,
                                                                            'id',
                                                                        ]}
                                                                        {...restField}>
                                                                        <Input
                                                                            {...InputProps}
                                                                        />
                                                                    </Form.Item>
                                                                    <Form.Item
                                                                        noStyle
                                                                        label="返回值名称"
                                                                        name={[
                                                                            name,
                                                                            'name',
                                                                        ]}
                                                                        {...restField}>
                                                                        <Input
                                                                            {...InputProps}
                                                                        />
                                                                    </Form.Item>
                                                                </td>
                                                                <td>
                                                                    <Form.Item
                                                                        noStyle
                                                                        label="返回值类型"
                                                                        name={[
                                                                            name,
                                                                            'type',
                                                                        ]}
                                                                        {...restField}>
                                                                        <Select
                                                                            {...InputProps}
                                                                            options={
                                                                                Mcp.FieldTypeOptions
                                                                            }
                                                                        />
                                                                    </Form.Item>
                                                                </td>
                                                                <td>
                                                                    <Form.Item
                                                                        noStyle
                                                                        label="描述"
                                                                        name={[
                                                                            name,
                                                                            'description',
                                                                        ]}
                                                                        {...restField}>
                                                                        <Input.TextArea
                                                                            {...InputProps}
                                                                            rows={
                                                                                1
                                                                            }
                                                                            autoSize
                                                                        />
                                                                    </Form.Item>
                                                                </td>
                                                                <td>
                                                                    <Form.Item
                                                                        noStyle
                                                                        label="示例值"
                                                                        name={[
                                                                            name,
                                                                            'example',
                                                                        ]}
                                                                        {...restField}>
                                                                        <Input.TextArea
                                                                            {...InputProps}
                                                                            rows={
                                                                                1
                                                                            }
                                                                            autoSize
                                                                        />
                                                                    </Form.Item>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        textAlign:
                                                                            'center',
                                                                    }}>
                                                                    <Form.Item
                                                                        noStyle
                                                                        label="必填"
                                                                        name={[
                                                                            name,
                                                                            'required',
                                                                        ]}
                                                                        initialValue
                                                                        {...restField}>
                                                                        <Switch />
                                                                    </Form.Item>
                                                                </td>
                                                                <td>
                                                                    <Form.Item
                                                                        noStyle
                                                                        initialValue={
                                                                            0
                                                                        }
                                                                        label="排序"
                                                                        name={[
                                                                            name,
                                                                            'sortOrder',
                                                                        ]}
                                                                        {...restField}>
                                                                        <InputNumber
                                                                            {...InputProps}
                                                                        />
                                                                    </Form.Item>
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        textAlign:
                                                                            'center',
                                                                    }}>
                                                                    <LinkButton
                                                                        onClick={() => {
                                                                            checkDelete(
                                                                                row,
                                                                                () => {
                                                                                    remove(
                                                                                        name,
                                                                                    );
                                                                                },
                                                                            );
                                                                        }}>
                                                                        {row?.id ? (
                                                                            '删除'
                                                                        ) : (
                                                                            <DeleteOutline
                                                                                style={{
                                                                                    fontSize: 19,
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </LinkButton>
                                                                </td>
                                                            </tr>
                                                        );
                                                    },
                                                )}
                                            </tbody>
                                        </table>
                                        <Button
                                            className={Style.addBtn}
                                            onClick={() => add()}
                                            icon={<PlusSquareOutlined />}>
                                            新增一行
                                        </Button>
                                    </div>
                                );
                            }}
                        </Form.List>
                    </Form>
                </Spin>
            </div>
        </Drawer>
    );
};

export default Index;
